import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, User, BadgeCheck, Phone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { BRAND } from "../lib/constants";
import { Brand } from "../components/layout/Layout";
import { Field, TextInput, ActionButton } from "../components/common/Field";

const HERO = "https://images.unsplash.com/photo-1586191552066-d52dd1e3af86?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", registro_profissional: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  if (user) return <Navigate to="/" replace />;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success("Bem-vindo ao sistema NILLO");
      nav("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Chassi de caminhão" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050B10]/95 via-[#050B10]/75 to-[#050B10]/40" />
        <div className="absolute inset-0 tactical-grid opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Brand />
          <div className="max-w-xl fade-up">
            <div className="font-mono text-xs tracking-[0.3em] text-[#FFD500]">RELATÓRIO DIGITAL · v1.0</div>
            <h1 className="font-display font-black uppercase text-5xl xl:text-6xl leading-[0.95] text-white mt-3">Alinhamento de chassis com precisão milimétrica</h1>
            <p className="text-slate-300 mt-5 leading-relaxed">Inspeção, medição por coordenadas X/Y/Z, mapa gráfico de desalinhamento e laudo técnico em PDF — tudo em um só fluxo.</p>
            <div className="mt-8 flex gap-6 font-mono text-xs text-slate-400">
              <span><span className="inline-block w-6 h-[2px] bg-[#3B8FE0] align-middle mr-2" />REFERÊNCIA</span>
              <span><span className="inline-block w-6 h-1 bg-[#FF0000] align-middle mr-2" />DESVIO</span>
              <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 align-middle mr-2" />DENTRO</span>
            </div>
          </div>
          <div className="font-display font-bold tracking-[0.2em] text-[#FFD500] text-sm">{BRAND.frase}</div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 py-12 bg-[#050B10]">
        <div className="lg:hidden mb-10"><Brand /></div>
        <div className="w-full max-w-md mx-auto fade-up">
          <div className="flex border border-[#1E2F45] rounded-sm p-1 mb-8">
            {[["login", "ENTRAR"], ["register", "CRIAR CONTA"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`auth-tab-${k}`} onClick={() => { setMode(k); setError(""); }}
                className={`flex-1 h-9 font-display font-bold tracking-wider rounded-sm transition-colors ${mode === k ? "bg-[#FFD500] text-[#050B10]" : "text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <h2 className="font-display font-black uppercase text-3xl text-white">{mode === "login" ? "Acesso do executor" : "Cadastro de executor"}</h2>
          <p className="text-sm text-slate-400 mt-1 mb-6">{mode === "login" ? "Entre para registrar e emitir relatórios de alinhamento." : "Cada executor possui suas próprias inspeções."}</p>
          <form onSubmit={submit} className="flex flex-col gap-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <Field label="Nome completo"><div className="relative"><User size={15} className="absolute left-3 top-2.5 text-slate-500" /><TextInput testId="register-name-input" value={form.name} onChange={set("name")} className="pl-9" required /></div></Field>
                <Field label="Registro profissional (opcional)"><div className="relative"><BadgeCheck size={15} className="absolute left-3 top-2.5 text-slate-500" /><TextInput testId="register-registro-input" value={form.registro_profissional} onChange={set("registro_profissional")} className="pl-9" /></div></Field>
              </>
            )}
            <Field label="E-mail"><div className="relative"><Mail size={15} className="absolute left-3 top-2.5 text-slate-500" /><TextInput testId="login-email-input" type="email" value={form.email} onChange={set("email")} className="pl-9" required /></div></Field>
            <Field label="Senha"><div className="relative"><Lock size={15} className="absolute left-3 top-2.5 text-slate-500" /><TextInput testId="login-password-input" type="password" value={form.password} onChange={set("password")} className="pl-9" required minLength={mode === "register" ? 6 : 1} /></div></Field>
            {error && <div data-testid="auth-error" className="text-sm text-amber-300 border border-amber-500/30 bg-amber-500/10 px-3 py-2 rounded-sm">{error}</div>}
            <ActionButton type="submit" testId="login-submit-button" disabled={loading} className="h-11 text-base mt-2">{loading ? "AGUARDE..." : mode === "login" ? "ENTRAR" : "CRIAR CONTA"}</ActionButton>
          </form>
          <div className="mt-10 pt-6 border-t border-[#1E2F45] flex items-center gap-2 font-mono text-xs text-slate-500">
            <Phone size={13} className="text-[#FFD500]" /> {BRAND.telefone} · ATENDIMENTO {BRAND.atendimento}
          </div>
        </div>
      </div>
    </div>
  );
}
